import React, { useEffect, useRef, useState } from "react";
import { ExternalLink, RefreshCw, BarChart3, CheckCircle2, AlertCircle, WifiOff } from "lucide-react";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { useApp } from "../../context/AppContext";
import { Panel, Overline } from "../common/Panel";

/* ThingSpeak channels:
   Ch-A (3287764): field1=V_L1 field2=V_L2 field3=V_L3
                   field4=I_L1 field5=I_L2 field6=I_L3 field7=W_L1 field8=W_L2
   Ch-B (3287770): field1=W_L3  field2=PF_L1 field3=PF_L2 field4=PF_L3
                   field5=Hz_L1 field6=Hz_L2 field7=Hz_L3 field8=kWh_Total
*/
const GS_SHEET_ID = "1_4sR5lyNkXI6wMRKJy0Vf1TKfMDi8R1kcBUn5RGvLu4";
const GS_REFRESH_MS = 5000;
const GS_ROWS = 120; // ~2 minutes at 1 row/sec
const TS_BASE  = "https://api.thingspeak.com/channels/";
const TS_CH_A  = 3287764;
const TS_CH_B  = 3287770;
const TS_KEY_A = "UOE9JB0Z827Q9G2H";
const TS_KEY_B = "OLJUZUDTKL1SDK0G";

const RANGES = [
  { val: "1h",  label: "Last 1h",       minutes: 60   },
  { val: "24h", label: "Last 24h",      minutes: 1440 },
  { val: "7d",  label: "Last 7 days",   days: 7       },
  { val: "2w",  label: "Last 2 weeks",  days: 14      },
  { val: "1mo", label: "Last 1 month",  days: 30      },
  { val: "2mo", label: "Last 2 months", days: 60      },
  { val: "3mo", label: "Last 3 months", days: 90      },
];

// Format X-axis labels based on the selected range
function fmtTime(iso, cfg) {
  const d = new Date(iso);
  if (cfg.minutes && cfg.minutes <= 1440) {
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  if (cfg.days && cfg.days <= 7) {
    return d.toLocaleDateString([], { month: "short", day: "numeric" }) + " " +
           d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }
  return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

const COLORS = { L1: "#ef4444", L2: "#f59e0b", L3: "#3b82f6", Total: "#10b981" };

// Find a column label matching any of the candidate strings (case/separator insensitive)
function findColName(cols, ...names) {
  const clean = (s) => s.toLowerCase().replace(/[_\s\-]/g, "");
  return cols.find((c) => names.some((n) => clean(c).includes(clean(n)))) ?? null;
}

// Parse gviz JSON from Google Sheets public endpoint
async function fetchSheetRows(limit) {
  const tq = encodeURIComponent(`select * order by A desc limit ${limit}`);
  const url = `https://docs.google.com/spreadsheets/d/${GS_SHEET_ID}/gviz/tq?tqx=out:json&tq=${tq}`;
  const res = await fetch(url);
  const text = await res.text();
  const json = JSON.parse(text.slice(text.indexOf("{"), text.lastIndexOf("}") + 1));
  const cols = json.table.cols.map((c) => c.label || c.id);
  const rows = (json.table.rows || []).reverse().map((row) => {
    const obj = { __timeFmt: row.c?.[0]?.f ?? "" };
    row.c?.forEach((cell, i) => {
      obj[cols[i]] = (cell && typeof cell.v === "number") ? cell.v : null;
    });
    return obj;
  });
  return { cols, rows };
}

const GS_RANGE_OPTIONS = [
  { val: "2m",  label: "Last 2 min",    rows: 120  },
  { val: "1h",  label: "Last 1h",       rows: 1000 },
  { val: "24h", label: "Last 24h",      rows: 1000 },
  { val: "7d",  label: "Last 7 days",   rows: 1000 },
  { val: "1mo", label: "Last 1 month",  rows: 1000 },
  { val: "3mo", label: "Last 3 months", rows: 1000 },
];

function GSheetSection({ pzemOnline, showPast, onShowPast }) {
  const [data, setData]   = useState(null);
  const [status, setStatus] = useState("loading");
  const [msg, setMsg]     = useState("Connecting to Google Sheets…");
  const [gsRange, setGsRange] = useState("2m");
  const gsRangeRef = useRef("2m");
  const timer = useRef(null);

  const load = async () => {
    try {
      const rows_limit = GS_RANGE_OPTIONS.find((r) => r.val === gsRangeRef.current)?.rows ?? GS_ROWS;
      const { cols, rows } = await fetchSheetRows(rows_limit);

      const cVL1 = findColName(cols, "V_L1","VL1","V1","volt1","voltage1","voltageL1");
      const cVL2 = findColName(cols, "V_L2","VL2","V2","volt2","voltage2","voltageL2");
      const cVL3 = findColName(cols, "V_L3","VL3","V3","volt3","voltage3","voltageL3");
      const cWL1 = findColName(cols, "W_L1","WL1","W1","pow1","power1","watt1");
      const cWL2 = findColName(cols, "W_L2","WL2","W2","pow2","power2","watt2");
      const cWL3 = findColName(cols, "W_L3","WL3","W3","pow3","power3","watt3");
      const cIL1 = findColName(cols, "I_L1","IL1","I1","curr1","current1","amp1");
      const cIL2 = findColName(cols, "I_L2","IL2","I2","curr2","current2","amp2");
      const cIL3 = findColName(cols, "I_L3","IL3","I3","curr3","current3","amp3");
      const cHz1 = findColName(cols, "Hz_L1","hz1","hz_l1");
      const cHz2 = findColName(cols, "Hz_L2","hz2","hz_l2");
      const cHz3 = findColName(cols, "Hz_L3","hz3","hz_l3");
      const cKwh = findColName(cols, "kWh_Total","kwh_total","kwh","energy");
      const cTmp = findColName(cols, "temp","dht_temp","temperature");
      const cHum = findColName(cols, "hum","humidity","rh");
      const cGas = findColName(cols, "gas_ppm","gas","ppm","mq135");
      const cPF1 = findColName(cols, "PF_L1","PFL1","pf_l1");
      const cPF2 = findColName(cols, "PF_L2","PFL2","pf_l2");
      const cPF3 = findColName(cols, "PF_L3","PFL3","pf_l3");

      const ts = (row) => {
        const f = row.__timeFmt;
        if (!f) return "--";
        const t = f.split(", ")[1] || f; // "M/D/YYYY, HH:MM:SS AM" → "HH:MM:SS AM"
        return t.replace(/ (AM|PM)$/, "");
      };

      const g = (row, col) => col ? row[col] ?? null : null;
      const last = rows[rows.length - 1];

      const voltData = rows.map((r) => ({ time: ts(r), L1: g(r, cVL1), L2: g(r, cVL2), L3: g(r, cVL3) }));
      const powData  = rows.map((r) => {
        const l1 = g(r, cWL1), l2 = g(r, cWL2), l3 = g(r, cWL3);
        return { time: ts(r), L1: l1, L2: l2, L3: l3, Total: (l1??0)+(l2??0)+(l3??0) || null };
      });
      const currData = rows.map((r) => ({ time: ts(r), L1: g(r, cIL1), L2: g(r, cIL2), L3: g(r, cIL3) }));
      const pfData   = rows.map((r) => ({ time: ts(r), L1: g(r, cPF1), L2: g(r, cPF2), L3: g(r, cPF3) }));

      const lastVals = last ? {
        wTotal: ((g(last,cWL1)??0)+(g(last,cWL2)??0)+(g(last,cWL3)??0)) || null,
        vL1: g(last, cVL1), vL2: g(last, cVL2), vL3: g(last, cVL3),
        hz: g(last, cHz1), kwh: g(last, cKwh), pf: g(last, cPF1),
        pfL2: g(last, cPF2), pfL3: g(last, cPF3),
      } : null;

      setData({ voltData, powData, currData, pfData, lastVals, rowCount: rows.length, lastTime: ts(last) });
      setStatus("ok");
      setMsg(`${rows.length} rows · last: ${ts(last)} · auto-refresh every 5s`);
    } catch (err) {
      setStatus("error");
      setMsg("Google Sheets error: " + (err.message || "check sheet sharing settings"));
    }
  };

  useEffect(() => {
    gsRangeRef.current = gsRange;
    clearInterval(timer.current);
    load();
    timer.current = setInterval(load, GS_REFRESH_MS);
    return () => clearInterval(timer.current);
  }, [gsRange]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Panel className="p-6">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-center gap-2">
          <BarChart3 size={16} className="text-[var(--accent)]" />
          <Overline>Google Sheets — Live Data</Overline>
        </div>
        <div className="flex items-center gap-2 overflow-x-auto hide-scrollbar">
          <button
            onClick={load}
            disabled={status === "loading"}
            title="Refresh now"
            className="shrink-0 w-8 h-8 rounded-md border border-[var(--bd)] flex items-center justify-center hover:border-[var(--bd-strong)] transition-colors disabled:opacity-50"
          >
            <RefreshCw size={13} className={status === "loading" ? "animate-spin" : ""} />
          </button>
          {GS_RANGE_OPTIONS.map(({ val, label }) => (
            <button
              key={val}
              onClick={() => setGsRange(val)}
              className={`shrink-0 px-3 py-1.5 rounded-md text-xs font-mono transition-colors ${gsRange === val ? "bg-[var(--accent)] text-black" : "bg-[var(--surface2)] border border-[var(--bd)] text-[var(--t2)] hover:text-[var(--t1)]"}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {!pzemOnline && !showPast ? (
        <div className="flex flex-col items-center justify-center py-10 gap-3 text-center">
          <WifiOff size={28} className="text-[var(--danger)] opacity-60" />
          <p className="font-mono text-sm text-[var(--t2)]">ESP32 offline — live feed paused.</p>
          <button onClick={onShowPast} className="px-4 py-2 rounded-md bg-[var(--surface2)] border border-[var(--bd)] text-xs font-mono hover:border-[var(--bd-strong)] transition-colors">View past data from Google Sheets</button>
        </div>
      ) : (<>

      <div className="flex items-center gap-2 mb-4 text-xs font-mono">
        {status === "ok"      && <CheckCircle2 size={12} className="text-[var(--accent)]" />}
        {status === "error"   && <AlertCircle  size={12} className="text-[var(--danger)]" />}
        {status === "loading" && <RefreshCw    size={12} className="animate-spin text-[var(--t2)]" />}
        <span className={status === "error" ? "text-[var(--danger)]" : "text-[var(--t2)]"}>{msg}</span>
      </div>

      {data?.lastVals && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          {[
            { label: "Total Power",   val: data.lastVals.wTotal != null ? Math.round(data.lastVals.wTotal) + " W"            : "--" },
            { label: "L1 Voltage",    val: data.lastVals.vL1   != null ? data.lastVals.vL1.toFixed(1) + " V"                 : "--" },
            { label: "Frequency",     val: data.lastVals.hz    != null ? data.lastVals.hz.toFixed(1) + " Hz"                 : "--" },
            { label: "Energy Total",  val: data.lastVals.kwh   != null ? data.lastVals.kwh.toFixed(3) + " kWh"               : "--" },
            { label: "L2 Voltage",    val: data.lastVals.vL2   != null ? data.lastVals.vL2.toFixed(1) + " V"                 : "--" },
            { label: "L3 Voltage",    val: data.lastVals.vL3   != null ? data.lastVals.vL3.toFixed(1) + " V"                 : "--" },
            { label: "PF L1",         val: data.lastVals.pf    != null ? data.lastVals.pf.toFixed(3)                         : "--" },
            { label: "PF L2",         val: data.lastVals.pfL2  != null ? data.lastVals.pfL2.toFixed(3)                       : "--" },
          ].map(({ label, val }) => (
            <div key={label} className="bg-[var(--surface2)] border border-[var(--bd)] rounded-md p-3 text-center">
              <div className="text-[10px] font-mono text-[var(--t2)] uppercase tracking-wide mb-1">{label}</div>
              <div className="font-mono font-bold text-sm text-[var(--accent)]">{val}</div>
            </div>
          ))}
        </div>
      )}

      {data ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ChartCard title={`Voltage — ${GS_RANGE_OPTIONS.find(r=>r.val===gsRange)?.label ?? "Last 2 min"}`} unit="V"   data={data.voltData} testid="gs-chart-v"
            lines={[{ key: "L1", label: "L1", color: COLORS.L1 }, { key: "L2", label: "L2", color: COLORS.L2 }, { key: "L3", label: "L3", color: COLORS.L3 }]} />
          <ChartCard title={`Power — ${GS_RANGE_OPTIONS.find(r=>r.val===gsRange)?.label ?? "Last 2 min"}`}   unit="W"   data={data.powData}  testid="gs-chart-w"
            lines={[{ key: "L1", label: "L1", color: COLORS.L1 }, { key: "L2", label: "L2", color: COLORS.L2 }, { key: "L3", label: "L3", color: COLORS.L3 }, { key: "Total", label: "Total", color: COLORS.Total }]} />
          <ChartCard title={`Current — ${GS_RANGE_OPTIONS.find(r=>r.val===gsRange)?.label ?? "Last 2 min"}`} unit="A"   data={data.currData} testid="gs-chart-i"
            lines={[{ key: "L1", label: "L1", color: COLORS.L1 }, { key: "L2", label: "L2", color: COLORS.L2 }, { key: "L3", label: "L3", color: COLORS.L3 }]} />
          <ChartCard title={`Power Factor — ${GS_RANGE_OPTIONS.find(r=>r.val===gsRange)?.label ?? "Last 2 min"}`} unit="" data={data.pfData} testid="gs-chart-pf"
            lines={[{ key: "L1", label: "L1", color: COLORS.L1 }, { key: "L2", label: "L2", color: COLORS.L2 }, { key: "L3", label: "L3", color: COLORS.L3 }]} />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {["v","w","i","pf"].map((k) => (
            <div key={k} className="bg-[var(--surface2)] border border-[var(--bd)] rounded-md p-4 h-[198px] flex items-center justify-center">
              <span className="font-mono text-xs text-[var(--t2)] animate-pulse">Connecting to Google Sheets…</span>
            </div>
          ))}
        </div>
      )}

      {!pzemOnline && showPast && (
        <p className="text-[11px] text-[var(--danger)] font-mono mt-2 opacity-70">Showing past cloud data — ESP32 is currently offline, new readings are not being sent.</p>
      )}
      <p className="text-[11px] text-[var(--t2)] font-mono mt-1">
        Columns: Timestamp · V_L1/2/3 · I_L1/2/3 · W_L1/2/3 · PF_L1/2/3 · Hz_L1/2/3 · kWh_Total · hardware uploads every 15s · frontend refreshes every 5s
      </p>
      </>)}
    </Panel>
  );
}

function ChartCard({ title, unit, data, lines, testid }) {
  return (
    <div className="bg-[var(--surface2)] border border-[var(--bd)] rounded-md p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-mono font-medium">{title}</span>
        <span className="text-[10px] font-mono text-[var(--t2)]">{unit}</span>
      </div>
      <ResponsiveContainer width="100%" height={150}>
        <LineChart data={data} margin={{ top: 4, right: 6, left: -20, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--bd)" />
          <XAxis
            dataKey="time"
            tick={{ fontSize: 9, fontFamily: "JetBrains Mono", fill: "var(--t2)" }}
            stroke="var(--bd)"
            interval="preserveStartEnd"
            minTickGap={40}
          />
          <YAxis
            tick={{ fontSize: 9, fontFamily: "JetBrains Mono", fill: "var(--t2)" }}
            stroke="var(--bd)"
            domain={["auto", "auto"]}
          />
          <Tooltip
            contentStyle={{ background: "var(--surface)", border: "1px solid var(--bd-strong)", borderRadius: 8, fontFamily: "JetBrains Mono", fontSize: 11 }}
          />
          <Legend wrapperStyle={{ fontSize: 10, fontFamily: "JetBrains Mono" }} />
          {lines.map((ln) => (
            <Line
              key={ln.key}
              type="monotone"
              dataKey={ln.key}
              name={ln.label}
              stroke={ln.color}
              strokeWidth={1.5}
              dot={false}
              isAnimationActive={false}
              connectNulls={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function LinkCard({ title, desc, children }) {
  return (
    <Panel className="p-5">
      <Overline>{title}</Overline>
      <p className="text-sm text-[var(--t2)] mt-1 mb-3">{desc}</p>
      <div className="flex flex-wrap gap-2">{children}</div>
    </Panel>
  );
}

function LinkBtn({ children, testid, onClick, href }) {
  const cls = "inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-mono font-medium bg-[var(--surface2)] border border-[var(--bd)] hover:border-[var(--bd-strong)] transition-colors";
  if (href) return <a href={href} target="_blank" rel="noreferrer" data-testid={testid} className={cls}>{children}<ExternalLink size={12} /></a>;
  return <button onClick={onClick} data-testid={testid} className={cls}>{children}</button>;
}

export default function Grafana() {
  const { settings, pzemOnline, lastUpdate } = useApp();
  const [showPast, setShowPast] = useState(false);
  const [range, setRange] = useState("1h");
  const [charts, setCharts] = useState(null);
  const [status, setStatus] = useState("idle");
  const [statusMsg, setStatusMsg] = useState("");
  const timerRef = useRef(null);
  const rangeRef = useRef(range);

  const loadData = async (r) => {
    const cur = r ?? rangeRef.current;
    const cfg = RANGES.find((x) => x.val === cur) ?? RANGES[0];
    // Use minutes= for ≤24h ranges, days= for longer — always cap results at 800 for readability
    const qParam = cfg.minutes != null ? `minutes=${cfg.minutes}` : `days=${cfg.days}`;
    const results = 800;
    setStatus("loading");
    setStatusMsg(`Fetching ThingSpeak data (${cfg.label})…`);
    try {
      const [resA, resB] = await Promise.all([
        fetch(`${TS_BASE}${TS_CH_A}/feeds.json?api_key=${TS_KEY_A}&${qParam}&results=${results}`).then((x) => x.json()),
        fetch(`${TS_BASE}${TS_CH_B}/feeds.json?api_key=${TS_KEY_B}&${qParam}&results=${results}`).then((x) => x.json()),
      ]);

      const feedA = resA.feeds || [];
      const feedB = resB.feeds || [];

      const p = (v) => { const n = parseFloat(v); return isNaN(n) ? null : n; };

      const voltageData = [], currentData = [], powerData = [], pfData = [], hzData = [], kwhData = [];
      const lenA = feedA.length;
      const lenB = feedB.length;

      feedA.forEach((fA, i) => {
        // align Ch-B by index offset so both channels line up even if timestamps differ
        const bOff = lenB - lenA;
        const fB = (bOff + i >= 0 && bOff + i < lenB) ? feedB[bOff + i] : {};
        const t = fmtTime(fA.created_at, cfg);
        const wl1 = p(fA.field7), wl2 = p(fA.field8), wl3 = p(fB.field1);
        const total = ((wl1 ?? 0) + (wl2 ?? 0) + (wl3 ?? 0)) || null;
        voltageData.push({ time: t, L1: p(fA.field1), L2: p(fA.field2), L3: p(fA.field3) });
        currentData.push({ time: t, L1: p(fA.field4), L2: p(fA.field5), L3: p(fA.field6) });
        powerData.push({ time: t, L1: wl1, L2: wl2, L3: wl3, Total: total });
        pfData.push({ time: t, L1: p(fB.field2), L2: p(fB.field3), L3: p(fB.field4) });
        hzData.push({ time: t, L1: p(fB.field5), L2: p(fB.field6), L3: p(fB.field7) });
        kwhData.push({ time: t, Total: p(fB.field8) });
      });

      const lastTime = voltageData[voltageData.length - 1]?.time ?? "--";
      setStatusMsg(`${feedA.length} points · last: ${lastTime} · auto-refresh every 15s`);
      setStatus("ok");
      setCharts({
        voltage:   { data: voltageData, lines: [{ key: "L1", label: "L1", color: COLORS.L1 }, { key: "L2", label: "L2", color: COLORS.L2 }, { key: "L3", label: "L3", color: COLORS.L3 }] },
        current:   { data: currentData, lines: [{ key: "L1", label: "L1", color: COLORS.L1 }, { key: "L2", label: "L2", color: COLORS.L2 }, { key: "L3", label: "L3", color: COLORS.L3 }] },
        power:     { data: powerData,   lines: [{ key: "L1", label: "L1", color: COLORS.L1 }, { key: "L2", label: "L2", color: COLORS.L2 }, { key: "L3", label: "L3", color: COLORS.L3 }, { key: "Total", label: "Total", color: COLORS.Total }] },
        pf:        { data: pfData,      lines: [{ key: "L1", label: "L1", color: COLORS.L1 }, { key: "L2", label: "L2", color: COLORS.L2 }, { key: "L3", label: "L3", color: COLORS.L3 }] },
        frequency: { data: hzData,      lines: [{ key: "L1", label: "L1", color: COLORS.L1 }, { key: "L2", label: "L2", color: COLORS.L2 }, { key: "L3", label: "L3", color: COLORS.L3 }] },
        kwh:       { data: kwhData,     lines: [{ key: "Total", label: "kWh Total", color: COLORS.Total }] },
      });
    } catch (err) {
      setStatus("error");
      setStatusMsg("ThingSpeak error: " + err.message);
    }
  };

  useEffect(() => {
    rangeRef.current = range;
    loadData(range);
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => loadData(), 15000);
    return () => clearInterval(timerRef.current);
  }, [range]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-6">
      {!pzemOnline && (
        <div className="rounded-lg border border-[var(--danger)]/40 bg-[var(--danger)]/10 overflow-hidden">
          <div className="flex items-center gap-3 px-4 py-3 text-sm font-mono text-[var(--danger)] flex-wrap">
            <WifiOff size={16} className="shrink-0" />
            <span>ESP32 offline — no data received in the last 15 seconds.</span>
            {lastUpdate && <span className="text-[var(--t2)] whitespace-nowrap">Last seen: {lastUpdate.toLocaleTimeString()}</span>}
            <button
              onClick={() => setShowPast((p) => !p)}
              className="ml-auto shrink-0 px-3 py-1 rounded-md bg-[var(--surface)] border border-[var(--danger)]/40 text-[var(--t2)] text-xs hover:border-[var(--danger)] transition-colors"
            >
              {showPast ? "Hide past data" : "View past data"}
            </button>
          </div>
        </div>
      )}
      <div>
        <Overline>Cloud Platforms</Overline>
        <h2 className="font-heading font-bold text-2xl tracking-tight mt-1">Data Integrations</h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <LinkCard title="Grafana Cloud" desc="Time-series dashboards.">
          <LinkBtn href={`${settings.grafanaUrl}?orgId=1&from=now-1h&to=now&refresh=15s`} testid="grafana-1h">Last 1h</LinkBtn>
          <LinkBtn href={`${settings.grafanaUrl}?orgId=1&from=now-24h&to=now&refresh=30s`} testid="grafana-24h">Last 24h</LinkBtn>
          <LinkBtn href={`${settings.grafanaUrl}?orgId=1&from=now-7d&to=now`} testid="grafana-7d">Last 7 days</LinkBtn>
          <LinkBtn href={`${settings.grafanaUrl}?orgId=1&from=now-14d&to=now`} testid="grafana-2w">Last 2 weeks</LinkBtn>
          <LinkBtn href={`${settings.grafanaUrl}?orgId=1&from=now-30d&to=now`} testid="grafana-1mo">Last 1 month</LinkBtn>
          <LinkBtn href={`${settings.grafanaUrl}?orgId=1&from=now-60d&to=now`} testid="grafana-2mo">Last 2 months</LinkBtn>
          <LinkBtn href={`${settings.grafanaUrl}?orgId=1&from=now-90d&to=now`} testid="grafana-3mo">Last 3 months</LinkBtn>
        </LinkCard>

        <LinkCard title="ThingSpeak" desc="IoT dual-channel analytics.">
          <LinkBtn href={`https://thingspeak.com/channels/${TS_CH_A}`} testid="thingspeak-a">Ch-A: V / I / W</LinkBtn>
          <LinkBtn href={`https://thingspeak.com/channels/${TS_CH_B}`} testid="thingspeak-b">Ch-B: PF / Hz / kWh</LinkBtn>
          <LinkBtn href={`${TS_BASE}${TS_CH_A}/feeds.json?api_key=${TS_KEY_A}&results=10`} testid="thingspeak-verify-a">
            <CheckCircle2 size={12} /> Verify Ch-A
          </LinkBtn>
          <LinkBtn href={`${TS_BASE}${TS_CH_B}/feeds.json?api_key=${TS_KEY_B}&results=10`} testid="thingspeak-verify-b">
            <CheckCircle2 size={12} /> Verify Ch-B
          </LinkBtn>
        </LinkCard>

        <LinkCard title="Google Sheets" desc="Live energy log — uploads every 5 s from ESP32.">
          <LinkBtn href={`https://docs.google.com/spreadsheets/d/${GS_SHEET_ID}/edit`} testid="google-sheets-open">Open Sheet</LinkBtn>
          <LinkBtn href={`https://docs.google.com/spreadsheets/d/${GS_SHEET_ID}/export?format=csv`} testid="google-sheets-csv">Download CSV</LinkBtn>
          <LinkBtn href="https://script.google.com/macros/s/AKfycbwK3acHGdImSd5K6OUrxkU435zHEmmmqoiC75IVYmzY-fAEGQKpFceUjoKwq8gPYISQqA/exec" testid="apps-script">Apps Script</LinkBtn>
          <LinkBtn href="https://public.tableau.com/views/CampusEnergyMonitor/Dashboard1?:language=en-US&:sid=&:redirect=auth&:display_count=n&:origin=viz_share_link" testid="tableau">Tableau</LinkBtn>
        </LinkCard>
      </div>

      <GSheetSection pzemOnline={pzemOnline} showPast={showPast} onShowPast={() => setShowPast(true)} />

      <Panel className="p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div className="flex items-center gap-2">
            <BarChart3 size={16} className="text-[var(--accent)]" />
            <Overline>ThingSpeak Historical Trends</Overline>
          </div>
          <div className="flex items-center gap-2 overflow-x-auto hide-scrollbar">
            <button
              onClick={() => loadData(range)}
              disabled={status === "loading"}
              className="shrink-0 w-8 h-8 rounded-md border border-[var(--bd)] flex items-center justify-center hover:border-[var(--bd-strong)] transition-colors disabled:opacity-50"
            >
              <RefreshCw size={13} className={status === "loading" ? "animate-spin" : ""} />
            </button>
            {RANGES.map(({ val, label }) => (
              <button
                key={val}
                onClick={() => setRange(val)}
                data-testid={`history-range-${val}`}
                className={`shrink-0 px-3 py-1.5 rounded-md text-xs font-mono transition-colors ${range === val ? "bg-[var(--accent)] text-black" : "bg-[var(--surface2)] border border-[var(--bd)] text-[var(--t2)] hover:text-[var(--t1)]"}`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {!pzemOnline && !showPast ? (
          <div className="flex flex-col items-center justify-center py-10 gap-3 text-center">
            <WifiOff size={28} className="text-[var(--danger)] opacity-60" />
            <p className="font-mono text-sm text-[var(--t2)]">ESP32 offline — live feed paused.</p>
            <button onClick={() => setShowPast(true)} className="px-4 py-2 rounded-md bg-[var(--surface2)] border border-[var(--bd)] text-xs font-mono hover:border-[var(--bd-strong)] transition-colors">View past data from ThingSpeak</button>
          </div>
        ) : (<>

        <div className="flex items-center gap-2 mb-4 text-xs font-mono">
          {status === "ok"    && <CheckCircle2 size={12} className="text-[var(--accent)]" />}
          {status === "error" && <AlertCircle size={12} className="text-[var(--danger)]" />}
          {status === "loading" && <RefreshCw size={12} className="animate-spin text-[var(--t2)]" />}
          <span className={status === "error" ? "text-[var(--danger)]" : "text-[var(--t2)]"}>{statusMsg}</span>
        </div>

        {charts ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <ChartCard title="Voltage"      unit="V"   data={charts.voltage.data}   lines={charts.voltage.lines}   testid="chart-voltage" />
            <ChartCard title="Current"      unit="A"   data={charts.current.data}   lines={charts.current.lines}   testid="chart-current" />
            <ChartCard title="Power"        unit="W"   data={charts.power.data}     lines={charts.power.lines}     testid="chart-power" />
            <ChartCard title="Power Factor" unit=""    data={charts.pf.data}        lines={charts.pf.lines}        testid="chart-pf" />
            <ChartCard title="Frequency"    unit="Hz"  data={charts.frequency.data} lines={charts.frequency.lines} testid="chart-frequency" />
            <ChartCard title="Energy"       unit="kWh" data={charts.kwh.data}       lines={charts.kwh.lines}       testid="chart-kwh" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {["voltage", "current", "power", "pf", "frequency", "kwh"].map((k) => (
              <div key={k} className="bg-[var(--surface2)] border border-[var(--bd)] rounded-md p-4 h-[198px] flex items-center justify-center" data-testid={`chart-${k}`}>
                <span className="font-mono text-xs text-[var(--t2)] animate-pulse">Loading ThingSpeak…</span>
              </div>
            ))}
          </div>
        )}

        {!pzemOnline && showPast && (
          <p className="text-[11px] text-[var(--danger)] font-mono mt-2 opacity-70">Showing past cloud data — ESP32 is currently offline, new readings are not being sent.</p>
        )}
        <p className="text-[11px] text-[var(--t2)] font-mono mt-1">
          Data from ThingSpeak Ch-A ({TS_CH_A}) &amp; Ch-B ({TS_CH_B}) · updates every 15 s from ESP32 · auto-refresh every 15 s
        </p>
        </>)}
      </Panel>
    </div>
  );
}
